import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import api from '@/lib/api';

interface Order {
  id: string;
  number: string;
  channel: string;
  status: string;
  total: number;
  createdAt: string;
  customer: { name: string };
}

const statusLabels: Record<string, { label: string; color: string }> = {
  RECEBIDO: { label: 'Recebido', color: 'bg-gray-100 text-gray-700' },
  PAGO: { label: 'Pago', color: 'bg-blue-100 text-blue-700' },
  EM_SEPARACAO: { label: 'Em Separação', color: 'bg-yellow-100 text-yellow-700' },
  SEPARADO: { label: 'Separado', color: 'bg-purple-100 text-purple-700' },
  EMBALADO: { label: 'Embalado', color: 'bg-indigo-100 text-indigo-700' },
  EXPEDIDO: { label: 'Expedido', color: 'bg-orange-100 text-orange-700' },
  EM_TRANSITO: { label: 'Em Trânsito', color: 'bg-cyan-100 text-cyan-700' },
  ENTREGUE: { label: 'Entregue', color: 'bg-green-100 text-green-700' },
  CANCELADO: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
};

export default function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    const params = statusFilter ? `?status=${statusFilter}` : '';
    api.get(`/orders${params}`)
      .then((r) => {
        setOrders(r.data.data);
        setLoadingData(false);
      })
      .catch(() => setLoadingData(false));
  }, [statusFilter]);

  return (
    <>
    <div className="flex items-center gap-4 mb-6">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">Todos os Status</option>
          {Object.entries(statusLabels).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {loadingData ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-dark-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-dark-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Número</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Canal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Cliente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-dark-500 uppercase">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Data</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-100">
              {orders.map((order, index) => (
                <tr
                  key={order.id}
                  className={`hover:bg-primary-50 cursor-pointer transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-dark-50/50'}`}
                  onClick={() => navigate(`/orders/${order.id}`)}
                >
                  <td className="px-6 py-4 text-sm font-medium text-primary-600">{order.number}</td>
                  <td className="px-6 py-4 text-sm text-dark-600">{order.channel}</td>
                  <td className="px-6 py-4 text-sm text-dark-900">{order.customer?.name || '-'}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${statusLabels[order.status]?.color || 'bg-gray-100 text-gray-700'}`}>
                      {statusLabels[order.status]?.label || order.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-dark-900 text-right">
                    {order.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </td>
                  <td className="px-6 py-4 text-sm text-dark-500">
                    {new Date(order.createdAt).toLocaleDateString('pt-BR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {orders.length === 0 && (
            <div className="text-center py-12 text-dark-500">Nenhum pedido encontrado</div>
          )}
        </div>
      )}
    </>
  );
}
